import { useEffect, useState } from 'react';
import { gnssState, gnssLive, averageGnss, gnssMeta, getGnssCfg } from './gnss';

// RTK precision engine: profiles "survey" (only RTK FIX passes, FLOAT = justified exception) and "plan" (everything allowed, everything labelled)
export const HRMS_OPTS = [0.02, 0.05, 0.1, 0.5];
export const PROFILES = ['survey', 'plan'];
export const EXC_REASONS = ['shade', 'multi', 'corr', 'time', 'other'];
export const extRtk = (s = gnssState()) => !!s.connected && s.mode !== 'system';
export const precLevel = (s = gnssState()) => (!extRtk(s) || !gnssLive(s) ? 'none' : s.fix === 'rtk' ? 'rtk' : s.fix === 'float' ? 'float' : 'gps');
export const precProfile = () => (getGnssCfg().prec === 'plan' ? 'plan' : 'survey');
export const strictOn = () => precProfile() === 'survey';
export const hrmsMax = () => +getGnssCfg().hrmsMax || 0.05;

// dialog store (one prompt at a time)
let cur = null;
const subs = new Set();
const put = (v) => { cur = v; subs.forEach((f) => f(v)); };
export function usePrecUi() {
  const [v, setV] = useState(cur);
  useEffect(() => { subs.add(setV); return () => subs.delete(setV); }, []);
  return v;
}
export const precAsk = (kind, info = {}) => new Promise((res) => put({ kind, info, res: (r) => { put(null); res(r); } }));

// survey profile: deviation from "RTK FIX + known accuracy within limit" needs a stated reason
const except = async (why, info = {}) => {
  const r = await precAsk('except', { why, ...info });
  return r && r.reason ? r.reason : null;
};

// measured point from the external receiver: { gnss, draft } or null (cancelled / blocked)
export async function measurePrecise() {
  for (;;) {
    const lv = precLevel(), prof = precProfile();
    if (lv === 'none') { await precAsk('blocked', { lost: true }); return null; }
    const why = [];
    if (prof === 'survey') {
      if (lv === 'gps') { await precAsk('blocked', { survey: true }); return null; }
      if (lv === 'float') why.push('float');
    }
    const n = +getGnssCfg().avg || 1;
    put({ kind: 'busy', info: { n } });
    const a = await averageGnss(n);
    put(null);
    if (a.incomplete || a.lat == null) {
      if ((await precAsk('fixlost', { lost: true })) === 'retry') continue;
      return null;
    }
    if (prof === 'survey' && a.fixChanged && a.fix !== 'rtk' && !why.includes('float')) {
      if ((await precAsk('fixlost', { fix: a.fix })) === 'retry') continue;
      return null;
    }
    if (a.maxHrms == null) why.push('noacc');
    else if (a.maxHrms > hrmsMax() && prof === 'survey' && !why.length) {
      const r = await precAsk('retake', { v: a.maxHrms, max: hrmsMax() });
      if (r === 'retry') continue;
      if (r !== 'ok') return null;
      why.push('hrms');
    }
    let qs = prof === 'plan' ? 'plan' : 'ok', reason;
    if (prof === 'survey' && why.length) {
      reason = await except(why[0], { v: a.maxHrms, max: hrmsMax() });
      if (!reason) return null;
      qs = 'exception';
    }
    return { gnss: gnssMeta(a, { profile: prof, qs, reason }), draft: lv === 'gps' };
  }
}

// gate for a tapped point: receiver connected -> choose RTK / tap; otherwise warning + draft
export async function gatePoint(t, pt) {
  if (precLevel() === 'none') return { pt, draft: true, warn: true };
  const c = await precAsk('choose');
  if (c === 'tap') return { pt, draft: true };
  if (c !== 'rtk') return null;
  const r = await measurePrecise();
  return r ? { pt: [r.gnss.lat, r.gnss.lon], gnss: r.gnss, draft: r.draft } : null;
}
